<template>
  <v-dialog
    :value="value"
    :max-width="maxWidth"
    overlay-color="var(--ds-color-reverse)"
    @input="closeDialog"
  >
    <v-card>
      <v-toolbar>
        <v-toolbar-title>
          {{ filterTitleLabel }}
        </v-toolbar-title>
        <v-spacer />
        <v-toolbar-items class="align-items-center">
          <span>
            <StIcon
              class="close-icon"
              icon-name="close"
              @click="closeDialog"
            />
          </span>
        </v-toolbar-items>
      </v-toolbar>

      <v-row
        no-gutters
        class="mt-5 mx-5"
      >
        <GlobalFilterSummary
          v-if="showGlobalFilterFeature"
          :global-filter-by-data-type="globalFilterByDataType"
          :data-type="card.dataType"
        />
      </v-row>

      <v-row
        no-gutters
        class="mt-5 mx-5"
      >
        <v-col
          cols="12"
          md="4"
          class="pr-4"
          @scroll.stop
        >
          <ContractFilter
            v-model="tempFilter['contracts']"
            :searchable="true"
            :items="contractFilterItems"
            @input="changeFilterValue()"
            @clearInfo="trackEvent"
          />
        </v-col>

        <v-col
          cols="12"
          md="4"
          class="pr-4"
          @scroll.stop
        >
          <BotFilter
            v-model="tempFilter['bots']"
            :searchable="true"
            :items="botFilterItems"
            @input="changeFilterValue()"
            @clearInfo="trackEvent"
          />
        </v-col>
        <v-col
          v-if="isEventComponent"
          cols="12"
          md="4"
          class="pr-4"
          @scroll.stop
        >
          <CategoryFilter
            v-model="tempFilter['category']"
            :filter="botSelected"
            :contract="getContract"
            :bot="getBot"
            :disabled="hasBotAndContractFiltered"
            @input="changeFilterValue()"
            @clearInfo="trackEvent"
          />
        </v-col>
        <v-col
          v-if="supportAudienceFilter"
          cols="12"
          md="4"
          class="pr-4"
          @scroll.stop
        >
          <ScheduledFilter
            v-model="tempFilter['scheduled']"
            :label="$t('components.filter.ModalFilters.label.blip.scheduling')"
            :contracts="tempFilter.contracts"
            :bots="tempFilter.bots"
            :disabled="validateBotsAndContractFiltered"
            :placeholder="validateScheduledPlaceHolder"
            @input="changeFilterValue()"
            @clearInfo="trackEvent"
          />
        </v-col>
        <v-col
          v-if="supportAudienceFilter"
          cols="12"
          md="4"
          class="pr-4"
          @scroll.stop
        >
          <CampaignNameFilter
            v-model="tempFilter['campaign_name']"
            :label="$t('components.filter.ModalFilters.label.blip.campaignName')"
            :contracts="tempFilter.contracts"
            :bots="tempFilter.bots"
            :disabled="validateBotsAndContractFiltered"
            @input="changeFilterValue()"
            @clearInfo="trackEvent"
          />
        </v-col>
      </v-row>
      <v-row
        v-if="!notSupportedFilter"
        no-gutters
        class="mt-5 mx-5"
      >
        <v-col
          cols="12"
          md="4"
          class="pr-4"
          @scroll.stop
        >
          <DomainsFilter
            v-model="tempFilter['domains']"
            :label="$t('components.filter.ModalFilters.label.blip.channels')"
            :select-items="domainsFilterItems"
            @input="changeFilterValue()"
            @clearInfo="trackEvent"
          />
          <v-col
            cols="12"
            class="operators-teams"
            @scroll.stop
          >
            <TeamsFilter
              v-model="tempFilter['teams']"
              :label="$t('components.filter.ModalFilters.label.blip.queues')"
              :contracts="tempFilter.contracts"
              :bots="tempFilter.bots"
              @input="changeFilterValue()"
              @clearInfo="trackEvent"
            />
          </v-col>
        </v-col>
        <v-col
          v-if="!notSupportedOperators"
          cols="12"
          md="4"
          class="pr-4"
          @scroll.stop
        >
          <v-col
            md=""
            class="operators-teams"
            @scroll.stop
          >
            <OperatorsFilter
              v-model="tempFilter['operators']"
              :label="$t('components.filter.ModalFilters.label.blip.attendants')"
              :contracts="tempFilter.contracts"
              :bots="tempFilter.bots"
              @input="changeFilterValue()"
              @clearInfo="trackEvent"
            />
          </v-col>
        </v-col>
        <v-col
          cols="12"
          md="4"
          class="pr-4"
          @scroll.stop
        >
          <DeskTagsFilter
            v-model="tempFilter['tags']"
            :label="$t('components.filter.ModalFilters.label.blip.tags')"
            :contracts="tempFilter.contracts"
            :bots="tempFilter.bots"
            @input="changeFilterValue()"
            @clearInfo="trackEvent"
          />
        </v-col>
      </v-row>
      <v-row
        no-gutters
        class="pb-5 px-5"
      >
        <v-spacer />
        <v-spacer />
        <v-btn
          v-if="showClearChanges"
          outlined
          rounded
          class="mr-3"
          @click="clearChangesFilter"
        >
          {{ clearChangeLabel }}
        </v-btn>
        <v-btn
          v-if="showClearFilter"
          outlined
          rounded
          class="mr-3"
          @click="clearFilter"
        >
          {{ clearFilterLabel }}
        </v-btn>
        <v-btn
          outlined
          rounded
          @click="applyFilter"
        >
          {{ applyLabel }}
        </v-btn>
      </v-row>
    </v-card>
  </v-dialog>
</template>

<script>
import StIcon from '@stilingue/st-icon';
import ContractFilter from '@/components/filter/ContractFilter';
import BotFilter from '@/components/filter/BotFilter';
import CategoryFilter from '@/components/filter/CategoryFilter';
import DomainsFilter from '@/components/filter/DomainsFilter';
import OperatorsFilter from '@/components/filter/OperatorsFilter';
import TeamsFilter from '@/components/filter/TeamsFilter';
import DeskTagsFilter from '@/components/filter/DeskTagsFilter';
import ScheduledFilter from '@/components/filter/ScheduledFilter';
import CampaignNameFilter from '@/components/filter/CampaignNameFilter';
import blipFilterModel from '@/mixins/blip-filter-model';
import filtersModel from '@/mixins/filters-model';
import blipModel from '@/mixins/blip-model';
import GlobalFilterSummary from '@/components/GlobalFilterSummary';
import { trackEventWithUserProperties } from '@/utils/mixpanel';
import { isEqual } from 'lodash';

import { Flags } from '@/plugins/rollout';
import { mapGetters, mapState } from 'vuex';
import { DATA_TYPE } from '@/store/modules/cards/constants';

export default {
  name: 'FilterBlipModal',
  components: {
    StIcon,
    ContractFilter,
    BotFilter,
    CategoryFilter,
    GlobalFilterSummary,
    DomainsFilter,
    OperatorsFilter,
    TeamsFilter,
    DeskTagsFilter,
    ScheduledFilter,
    CampaignNameFilter,
  },
  mixins: [
    blipFilterModel,
    filtersModel,
    blipModel,
  ],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    card: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      appliedFilter: {},
      tempFilter: {},
      maxBotsAllowed: 1,
    };
  },
  computed: {
    filterLabel() {
      return this.$t('components.filter.ModalFilters.label.blip.title');
    },
    applyLabel() {
      return this.$t('components.filter.ModalFilters.label.button.applyLabel');
    },
    clearFilterLabel() {
      return this.$t('components.filter.ModalFilters.label.button.clearFilter');
    },
    clearChangeLabel() {
      return this.$t('components.filter.ModalFilters.label.button.clearchange');
    },
    ...mapState('filter', [
      'globalFilter',
    ]),
    ...mapGetters('dashboard', [
      'activeDashboardFilters',
    ]),
    ...mapGetters('featureFlag', [
      'isEnabled',
    ]),
    ...mapGetters('user', [
      'isEditor',
    ]),
    botSelected() {
      return this.tempFilter;
    },
    getContract() {
      return this.getPropertyValue(this.tempFilter, 'contracts', 'value') || this.globalFilter?.contracts?.value;
    },
    getBot() {
      return this.getPropertyValue(this.tempFilter, 'bots', 'value') || this.globalFilter?.bots?.value;
    },
    showGlobalFilterFeature() {
      return this.isEnabled(Flags.showGlobalFilter) && this.isEditor;
    },
    showClearChanges() {
      return !(isEqual(this.appliedFilter, this.tempFilter)) || this.changeOnPageFilter;
    },
    isEventComponent() {
      return Boolean(this.card?.isEvent);
    },
    notSupportedFilter() {
      return Boolean(this.card?.desk?.notSupportDeskFilters);
    },
    notSupportedOperators() {
      return Boolean(this.card?.desk?.notSupportedOperators);
    },
    hasBotAndContractFiltered() {
      return Boolean(!Boolean(this.globalFilter?.contracts || this.tempFilter?.contracts) || !Boolean(this.globalFilter?.bots || this.tempFilter?.bots) || this.hasMoreThanOneBot);
    },
    validateScheduledPlaceHolder() {
      return (!Boolean(this.globalFilter?.contracts) ? 'Todas' : 'Nenhuma');
    },
    validateBotsAndContractFiltered() {
      return Boolean(!Boolean(this.globalFilter?.contracts || this.tempFilter?.contracts) || !Boolean(this.globalFilter?.bots || this.tempFilter?.bots));
    },
    hasMoreThanOneBot() {
      const hasMoreThanOneBot = Boolean(this.tempFilter?.bots?.value.length > this.maxBotsAllowed) || Boolean(this.globalFilter?.bots?.value.length > this.maxBotsAllowed);
      const hasOneIndividualBotAndMoreThanOneGlobal = Boolean(this.tempFilter?.bots?.value.length === this.maxBotsAllowed) && Boolean(this.globalFilter?.bots?.value.length > this.maxBotsAllowed);

      if (hasOneIndividualBotAndMoreThanOneGlobal) {
        return false;
      }
      return hasMoreThanOneBot;
    },
    supportAudienceFilter() {
      return Boolean(this.card?.audience?.suportScheduledFilters);
    },
  },
  watch: {
    'tempFilter.contracts': {
      handler(newContract, oldContract) {
        if (Boolean(oldContract?.value) && newContract.value !== oldContract?.value) {
          delete this.tempFilter.bots;
        }
      },
      deep: true,
    },
    'tempFilter.bots': {
      handler(newBots, oldBots) {
        if (Boolean(oldBots?.value) && newBots.value !== oldBots?.value) {
          delete this.tempFilter.teams;
          delete this.tempFilter.operators;
          delete this.tempFilter.tags;
        }
      },
      deep: true,
    },
  },
  methods: {
    trackEvent(label) {
      trackEventWithUserProperties('ST Card Filter Delete', { Field: label, 'Data Type': DATA_TYPE.BLIP });
    },
    changeFilterValue() {
      this.changeOnPageFilter = true;
    },
  },
};
</script>

<style scoped lang="scss">
  .operators-teams {
    padding: 0 !important;
  }

  .filter-sac-status-modal {
    line-height: 20px;
    color: var(--ds-color-text-1);
    font-size: var(--ds-font-size-sm);

    &__description {
      font-weight: var(--ds-font-weight-regular);
    }

    &__contrast {
      font-weight: var(--ds-font-weight-bold);
    }
  }

  .align-items-center {
    align-items: center;
  }
</style>
